/**
 * Cmarkdownxychart — download dropdown
 *
 * Thin wrapper around the project's Cdropdown using a Ghost Cbutton as the
 * trigger and a two-option menu (CSV / PNG). Reuses Cdropdown's keyboard
 * navigation, outside-click dismissal, and portal positioning so the chart
 * matches the rest of the app's menu surfaces.
 */

'use client';

import { type CSSProperties, useCallback, useMemo, useState } from 'react';
import { IconDownload, IconFileTypeCsv, IconPhoto } from '@tabler/icons-react';
import { Cbutton } from '../Cbutton';
import { Cdropdown, type CdropdownOption } from '../Cdropdown';
import { ButtonPreset } from '@/constants/componentPresets';

const ICON_BTN_SIZE = 32;
const ICON_BTN_ICON_SIZE = 18;
const ICON_BTN_RADIUS = 6;
const ICON_BTN_CONTAINER_STYLE: CSSProperties = {
  width: ICON_BTN_SIZE,
  minWidth: ICON_BTN_SIZE,
};
const MENU_WIDTH = 220;
const MENU_OPTION_KEY_CSV = 'csv';
const MENU_OPTION_KEY_PNG = 'png';
const MENU_OPTION_LABEL_CSV = 'Download CSV';
const MENU_OPTION_LABEL_PNG = 'Download Image';
const DROPDOWN_ALIGN_RIGHT = 'right';

export interface XYChartDownloadMenuProps {
  onDownloadCsv: () => void;
  onDownloadImage: () => void;
  disabled?: boolean;
}

export function XYChartDownloadMenu({
  onDownloadCsv,
  onDownloadImage,
  disabled = false,
}: XYChartDownloadMenuProps) {
    const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setIsOpen(nextOpen);
  }, []);

  const options: CdropdownOption[] = useMemo(
    () => [
      {
        key: MENU_OPTION_KEY_CSV,
        label: MENU_OPTION_LABEL_CSV,
        icon: IconFileTypeCsv,
        onClick: onDownloadCsv,
        disabled,
      },
      {
        key: MENU_OPTION_KEY_PNG,
        label: MENU_OPTION_LABEL_PNG,
        icon: IconPhoto,
        onClick: onDownloadImage,
        disabled,
      },
    ],
    [disabled, onDownloadCsv, onDownloadImage]
  );

  return (
    <Cdropdown
      dataElScope="Cmarkdownxychart/XYChartDownloadMenu"

      trigger={
        <Cbutton
          rootAttributes={{ "data-el": "Cmarkdownxychart/DownloadMenu/Trigger" }}
          icon={IconDownload}
          iconSize={ICON_BTN_ICON_SIZE}
          preset={ButtonPreset.Ghost}
          radius={ICON_BTN_RADIUS}
          padding={0}
          height={ICON_BTN_SIZE}
          containerStyle={ICON_BTN_CONTAINER_STYLE}
          disabled={disabled}
        />
      }
      options={options}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      align={DROPDOWN_ALIGN_RIGHT}
      width={MENU_WIDTH}
    />
  );
}
